import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  DollarSign,
  MapPin,
  Car,
  Clock,
  Eye,
  TrendingUp,
  TrendingDown,
  BarChart3,
  FileText,
  Activity,
} from "lucide-react";
import DataTablePagination from "@/components/ui/data-table-pagination";
import {
  usePricings,
  useDeletePricing,
  useTogglePricingStatus,
  useCities,
} from "@/api-services/hooks/usePricing";
import { BasePricing, StatusOption, CityMaster } from "@/api-services/hooks/pricingTypes";
import { PagedData } from "@/api-services/hooks/types";
import DeletePricingConfirmation from "@/components/pricing/DeletePricingConfirmation";

const PricingPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // Add query client for cache management
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusOption>("all");

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [pricingToDelete, setPricingToDelete] = useState<{
    id: number;
    details: string;
  } | null>(null);

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Handle search input change with useCallback to prevent re-creation
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Determine if we need client-side filtering
  const hasClientSideFilters = statusFilter !== "all";

  // When client-side filters are active, fetch a large page size to have enough data to filter
  // Otherwise, use normal pagination
  const apiPageSize = hasClientSideFilters ? 1000 : itemsPerPage;
  const apiPage = hasClientSideFilters ? 0 : currentPage; // Always fetch from page 0 when filtering client-side

  // Reset to first page when search filter changes (client-side filters don't reset page)
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  // Fetch pricings from API (only search filter goes to API, status filter is client-side)
  // Only send search to API if search term is more than 3 characters
  const shouldSearch = debouncedSearchTerm.trim().length > 2;
  const { data, isLoading: loading, error, refetch } = usePricings(
    {
      page: apiPage,
      size: apiPageSize,
      ...(shouldSearch && { search: debouncedSearchTerm.trim() }),
    },
    {
      enabled: true, // Always enabled, but search param only included if > 3 chars
      onError: (err) => {
        toast.error(`Failed to load pricing: ${err.message}`);
      },
    }
  );

  // Fetch all cities to ensure we have the latest city names
  // This fixes the issue where updating a city name doesn't reflect in the pricing list
  // because the pricing object might contain a stale snapshot of the city
  const { data: citiesData } = useCities({
    enabled: true,
  });

  // Create a lookup map for city names
  const cityMap = useMemo(() => {
    const map = new Map<number, string>();
    if (citiesData) {
      (citiesData as CityMaster[]).forEach(city => {
        map.set(city.cityId, city.cityName);
      });
    }
    return map;
  }, [citiesData]);

  // Delete pricing hook
  const { mutate: deletePricing, isPending: isDeleting } = useDeletePricing({
    onSuccess: async (data, pricingId) => {
      toast.success("Pricing deleted successfully!");
      // Invalidate all pricings queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['pricings'] });
    },
    onError: (error, pricingId) => {
      toast.error(`Failed to delete pricing: ${error.message}`);
    },
  });

  // Toggle pricing status hook
  const { mutate: toggleStatus, isPending: isToggling } =
    useTogglePricingStatus({
      onSuccess: async (data, pricingId) => {
        toast.success("Pricing status updated successfully!");
        // Update only the changed row in the cache instead of refetching entire table
        queryClient.setQueriesData<PagedData<BasePricing>>(
          { queryKey: ['pricings'] },
          (oldData) => {
            if (!oldData?.content) return oldData;
            
            const updatedContent = oldData.content.map((pricing) =>
              pricing.pricingId === pricingId
                ? { ...pricing, isActive: !pricing.isActive }
                : pricing
            );
            
            return {
              ...oldData,
              content: updatedContent,
            };
          }
        );
      },
      onError: (error, pricingId) => {
        toast.error(`Failed to update pricing status: ${error.message}`);
      },
    });

  const getStatusBadge = useCallback((isActive: boolean) => {
    return isActive
      ? {
        variant: "default",
        className: "bg-success text-success-foreground",
        label: "Active",
      }
      : {
        variant: "secondary",
        className: "bg-muted text-muted-foreground",
        label: "Inactive",
      };
  }, []);

  const handlePricingAction = (
    action: string,
    pricingId: number,
    pricingDetails: string
  ) => {
    if (action === "Edit") {
      navigate(`/admin/base-pricing/edit/${pricingId}`);
    } else if (action === "View") {
      navigate(`/admin/base-pricing/view/${pricingId}`);
    } else if (action === "Toggle Status") {
      toggleStatus(pricingId);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (pricingId: number, pricingDetails: string) => {
    setPricingToDelete({ id: pricingId, details: pricingDetails });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = (pricingId: number) => {
    deletePricing(pricingId);
    setDeleteConfirmationOpen(false);
    setPricingToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setPricingToDelete(null);
  };

  // Filter pricings by status on the client side
  const pricings = useMemo(() => {
    const pricingData = data as PagedData<BasePricing> | undefined;
    if (!pricingData || !pricingData.content) return [];

    let filtered = pricingData.content;

    // Filter by status (active/inactive)
    if (statusFilter !== "all") {
      filtered = filtered.filter((pricing) => {
        if (statusFilter === "active") {
          return pricing.isActive === true;
        } else if (statusFilter === "inactive") {
          return pricing.isActive === false;
        }
        return true;
      });
    }

    return filtered;
  }, [data, statusFilter, hasClientSideFilters]);

  // Pagination calculations - use filtered count for client-side filters
  const pricingData = data as any;
  const totalFilteredItems = pricings.length;
  const totalItems = pricingData?.page?.totalElements ?? 0;
  // Calculate total pages based on filtered items
  const totalPages = useMemo(() => {
    if (hasClientSideFilters) {
      // Client-side filtering: paginate filtered results
      return totalFilteredItems > 0 ? Math.ceil(totalFilteredItems / itemsPerPage) : 0;
    }
    // Server-side pagination: use API response
    const pages = pricingData?.page?.totalPages ?? 0;
    // If totalPages is 0 but we have totalElements, calculate it
    if (pages === 0 && pricingData?.page?.totalElements && pricingData?.page?.totalElements > 0) {
      return Math.ceil(pricingData?.page?.totalElements / itemsPerPage);
    }
    // If totalPages is still 0 but we have content, calculate from content length
    if (pages === 0 && pricingData?.content && pricingData?.content.length > 0) {
      return Math.ceil(pricingData?.content.length / itemsPerPage);
    }
    return pages;
  }, [totalFilteredItems, itemsPerPage, hasClientSideFilters, pricingData]);

  // Paginated pricings for current page (when using client-side filtering)
  const paginatedPricings = useMemo(() => {
    if (!hasClientSideFilters) {
      // No client-side filtering, use API pagination directly
      return pricingData?.content ?? [];
    }
    // Client-side pagination for filtered results
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return pricings.slice(startIndex, endIndex);
  }, [pricings, currentPage, itemsPerPage, hasClientSideFilters, pricingData]);

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Check if current page is empty after data loads and navigate to previous page if needed
  useEffect(() => {
    if (!loading && pricingData && paginatedPricings.length === 0 && currentPage > 0 && totalPages > 0) {
      // Current page is empty, navigate to the last valid page
      const lastPage = Math.max(0, totalPages - 1);
      setCurrentPage(lastPage);
    }
  }, [loading, paginatedPricings.length, currentPage, totalPages, pricingData]);

  // Format pricing details for display - wrapped in useCallback to prevent infinite re-renders
  const getPricingDetails = useCallback((pricing: BasePricing) => {
    // Use the city name from our fresh lookup map if available, otherwise fall back to the one in pricing object
    const cityName = pricing.city?.cityId && cityMap.has(pricing.city.cityId)
      ? cityMap.get(pricing.city.cityId)
      : (pricing.city?.cityName || "Unknown City");

    return `${cityName} - ${pricing.vehicleCategoryId
        ? "Vehicle " + pricing.vehicleCategoryId
        : "Unknown Vehicle"
      }`;
  }, [cityMap]);

  const getCurrencyDisplay = useCallback((amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading pricing data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <TrendingDown className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Pricing
            </h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Pricing Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and configure base pricing for your ride-hailing service
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            className="bg-gradient-primary hover:opacity-90"
            onClick={() => navigate("/admin/base-pricing/add")}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Pricing
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search pricing by city or vehicle... (min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={handleSearchChange}
                autoComplete="off"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter === "all"
                    ? "All Status"
                    : statusFilter.charAt(0).toUpperCase() +
                    statusFilter.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setStatusFilter("all")}>
                  All Status
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("active")}>
                  Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("inactive")}>
                  Inactive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Pricing Rules (
            {loading
              ? "..."
              : hasClientSideFilters
                ? `${totalFilteredItems} filtered`
                : totalItems > 0 ? totalItems : (pricingData?.content?.length ?? 0)}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location & Vehicle</TableHead>
                  <TableHead>Base Fare</TableHead>
                  <TableHead>Rate per KM</TableHead>
                  <TableHead>Rate per Minute</TableHead>
                  <TableHead>Minimum Fare</TableHead>
                  <TableHead>Night Charges</TableHead>
                  <TableHead>Toll Fee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPricings.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {pricings.length === 0
                        ? "No pricing rules found"
                        : "No pricing rules match the current filters"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPricings.map((pricing) => {
                    const statusBadge = getStatusBadge(pricing.isActive);
                    const pricingDetails = getPricingDetails(pricing);

                    return (
                      <TableRow
                        key={pricing.pricingId}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">
                                {pricing.city?.cityId && cityMap.has(pricing.city.cityId)
                                  ? cityMap.get(pricing.city.cityId)
                                  : (pricing.city?.cityName || "Unknown City")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Car className="w-3 h-3" />
                              <span>
                                Vehicle ID: {pricing.vehicleCategoryId}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-semibold text-green-600">
                            {getCurrencyDisplay(pricing.baseFare)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-blue-600">
                            {getCurrencyDisplay(pricing.ratePerKm)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-orange-600">
                            {getCurrencyDisplay(pricing.ratePerMinute)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-purple-600">
                            {getCurrencyDisplay(pricing.minimumFare)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-indigo-600">
                            {getCurrencyDisplay(pricing.nightCharges)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              pricing.tollFeeApplicable
                                ? "default"
                                : "secondary"
                            }
                            className={
                              pricing.tollFeeApplicable
                                ? "bg-blue-100 text-blue-800"
                                : ""
                            }
                          >
                            {pricing.tollFeeApplicable
                              ? "Applicable"
                              : "Not Applicable"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {pricing.isActive ? (
                              <>
                                <ToggleRight className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => {
                                  navigate(
                                    `/admin/base-pricing/view/${pricing.pricingId || 0
                                    }`
                                  );
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handlePricingAction(
                                    "Edit",
                                    pricing.pricingId || 0,
                                    pricingDetails
                                  )
                                }
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Pricing
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handlePricingAction(
                                    "Toggle Status",
                                    pricing.pricingId || 0,
                                    pricingDetails
                                  )
                                }
                                disabled={isToggling}
                              >
                                {pricing.isActive ? (
                                  <>
                                    <ToggleLeft className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <ToggleRight className="w-4 h-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleDeleteClick(
                                    pricing.pricingId || 0,
                                    pricingDetails
                                  )
                                }
                                className="text-destructive"
                                disabled={isDeleting}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Pricing
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination - Always show if totalPages > 0, even if current page is empty */}
            {totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={hasClientSideFilters ? totalFilteredItems : totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Pricing Confirmation Modal */}
      <DeletePricingConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        pricingDetails={pricingToDelete?.details || ""}
        pricingId={pricingToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PricingPage;
